import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { versionContributors_softwareReleasesLaunchCard$key } from '@atlassian/jira-relay/src/__generated__/versionContributors_softwareReleasesLaunchCard.graphql';
import messages from './messages';

type Props = {
	jiraVersionContributors: versionContributors_softwareReleasesLaunchCard$key;
};

export const VersionContributors = ({ jiraVersionContributors }: Props) => {
	const { formatMessage } = useIntl();

	const contributorsData = useFragment(
		graphql`
			fragment versionContributors_softwareReleasesLaunchCard on JiraVersionContributorEdge
			@relay(plural: true) {
				node @required(action: THROW) {
					id
					name
					picture
					... on AtlassianAccountUser {
						extendedProfile {
							jobTitle
						}
					}
				}
			}
		`,
		jiraVersionContributors,
	);

	const avatarData = contributorsData.map((edge) => ({
		key: edge.node.id,
		name: edge.node.name,
		src: edge.node.picture,
	}));

	return avatarData.length <= 0 ? null : (
		<Container>
			<Text size="UNSAFE_small" weight="bold" color="color.text.subtle" as="p">
				{formatMessage(messages.contributorsLabel)}
			</Text>
			<AvatarGroup
				appearance="stack"
				data={avatarData}
				maxCount={4}
				size="small"
				overrides={{
					AvatarGroupItem: {
						render: (_, props) => (
							<AvatarItem
								avatar={<Avatar {...props.avatar} />}
								primaryText={props.avatar.name}
								secondaryText={
									contributorsData.find((edge) => edge.node.id === props.avatar.key)?.node
										.extendedProfile?.jobTitle ?? ''
								}
							/>
						),
					},
				}}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100', '8px'),
});
